import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { TrendingUp, Calculator } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CalculatorTransparency, transparencyData } from './CalculatorTransparency';

const SkogstillvaxtKalkylator = () => {
  const { language } = useLanguage();
  const [nuvarandeVolym, setNuvarandeVolym] = useState(150);
  const [tillvaxtPerAr, setTillvaxtPerAr] = useState(5);
  const [antalAr, setAntalAr] = useState(10);
  const [result, setResult] = useState(null);

  const content = {
    sv: {
      title: 'Skogstillväxt kalkylator',
      description: 'Beräkna framtida virkesvolym baserat på nuvarande volym och förväntad årlig tillväxt. Planera din skogsskötsel med tillväxtberäkningar.',
      currentVolume: 'Nuvarande volym (m³/ha)',
      growthRate: 'Tillväxt per år (%)',
      typicalGrowth: 'Typiskt: 3-7% beroende på bonitet',
      years: 'Antal år',
      calculate: 'Beräkna tillväxt',
      resultAfter: 'Resultat efter',
      yearsUnit: 'år',
      futureVolume: 'Framtida volym',
      totalGrowth: 'Total tillväxt',
      averageGrowth: 'Genomsnittlig tillväxt',
      percentIncrease: 'Procentuell ökning',
      volumeIncrease: 'Volymökning',
      today: 'idag',
      after: 'om'
    },
    de: {
      title: 'Waldzuwachs Rechner',
      description: 'Berechnen Sie das zukünftige Holzvolumen basierend auf aktuellem Volumen und erwartetem jährlichen Zuwachs.',
      currentVolume: 'Aktuelles Volumen (m³/ha)',
      growthRate: 'Zuwachs pro Jahr (%)',
      typicalGrowth: 'Typisch: 3-7% je nach Bonität',
      years: 'Anzahl Jahre',
      calculate: 'Zuwachs berechnen',
      resultAfter: 'Ergebnis nach',
      yearsUnit: 'Jahren',
      futureVolume: 'Zukünftiges Volumen',
      totalGrowth: 'Gesamtzuwachs',
      averageGrowth: 'Durchschnittlicher Zuwachs',
      percentIncrease: 'Prozentuale Zunahme',
      volumeIncrease: 'Volumenzunahme'
    },
    en: {
      title: 'Forest Growth Calculator',
      description: 'Calculate future timber volume based on current volume and expected annual growth.',
      currentVolume: 'Current volume (m³/ha)',
      growthRate: 'Growth per year (%)',
      typicalGrowth: 'Typical: 3-7% depending on site quality',
      years: 'Number of years',
      calculate: 'Calculate growth',
      resultAfter: 'Result after',
      yearsUnit: 'years',
      futureVolume: 'Future volume',
      totalGrowth: 'Total growth',
      averageGrowth: 'Average growth',
      percentIncrease: 'Percent increase',
      volumeIncrease: 'Volume increase'
    },
    se: {
      title: 'Meahccešaddama rehkenastin',
      description: 'Rehkenas boahtteváš muorravoluma dálááigge voluma ja vuordojuvvon jahkásaš šaddama vuođul.',
      currentVolume: 'Dálááigge voluma (m³/ha)',
      growthRate: 'Šaddama jahkái (%)',
      years: 'Jagiid lohku',
      calculate: 'Rehkenas šaddama',
      resultAfter: 'Boađus maŋŋá'
    }
  };

  const c = content[language] || content.sv;
  const transparency = transparencyData.tillvaxt?.[language] || transparencyData.tillvaxt?.sv;

  const calculateGrowth = () => {
    const framtidaVolym = nuvarandeVolym * Math.pow(1 + tillvaxtPerAr / 100, antalAr);
    const totalTillvaxt = framtidaVolym - nuvarandeVolym;
    const arligTillvaxtM3 = totalTillvaxt / antalAr;
    
    const yearlyData = [];
    for (let i = 0; i <= antalAr; i++) {
      yearlyData.push({
        year: i,
        volume: nuvarandeVolym * Math.pow(1 + tillvaxtPerAr / 100, i)
      });
    }
    
    setResult({
      framtidaVolym: framtidaVolym.toFixed(1),
      totalTillvaxt: totalTillvaxt.toFixed(1),
      arligTillvaxtM3: arligTillvaxtM3.toFixed(1),
      procentuellOkning: ((framtidaVolym / nuvarandeVolym - 1) * 100).toFixed(1),
      yearlyData
    });
  };

  // Build assumptions from current values
  const assumptions = result ? [
    { label: 'Nuvarande volym', value: nuvarandeVolym, unit: 'm³/ha' },
    { label: 'Tillväxt', value: tillvaxtPerAr, unit: '%/år' },
    { label: 'Tidsperiod', value: antalAr, unit: 'år' },
    { label: 'Beräkningsmodell', value: 'Exponentiell', unit: '' }
  ] : [];

  return (
    <Card className="w-full" data-testid="skogstillvaxt-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <TrendingUp className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="nuvarande">{c.currentVolume}</Label>
            <Input
              id="nuvarande"
              type="number"
              value={nuvarandeVolym}
              onChange={(e) => setNuvarandeVolym(Number(e.target.value))}
              min={1}
              data-testid="nuvarande-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="tillvaxt">{c.growthRate}</Label>
            <Input
              id="tillvaxt"
              type="number"
              step="0.5"
              value={tillvaxtPerAr}
              onChange={(e) => setTillvaxtPerAr(Number(e.target.value))}
              min={0}
              max={15}
              data-testid="tillvaxt-input"
            />
            <p className="text-xs text-muted-foreground">{c.typicalGrowth}</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="ar">{c.years}</Label>
            <Input
              id="ar"
              type="number"
              value={antalAr}
              onChange={(e) => setAntalAr(Number(e.target.value))}
              min={1}
              max={100}
              data-testid="ar-input"
            />
          </div>
        </div>

        <Button 
          onClick={calculateGrowth} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {c.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">{c.resultAfter} {antalAr} {c.yearsUnit}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{c.futureVolume}</p>
                <p className="text-2xl font-bold text-forest-700">{result.framtidaVolym} m³/ha</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.totalGrowth}</p>
                <p className="text-2xl font-bold text-green-600">+{result.totalTillvaxt} m³/ha</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.averageGrowth}</p>
                <p className="text-lg font-semibold text-forest-700">{result.arligTillvaxtM3} m³/ha/år</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.percentIncrease}</p>
                <p className="text-lg font-semibold text-green-600">+{result.procentuellOkning}%</p>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-forest-200">
              <p className="text-sm text-muted-foreground mb-2">{c.volumeIncrease}</p>
              <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-forest-500 to-forest-700 transition-all duration-500"
                  style={{ width: `${Math.min(100, (result.framtidaVolym / nuvarandeVolym) * 50)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{nuvarandeVolym} m³ ({c.today || 'idag'})</span>
                <span>{result.framtidaVolym} m³ ({c.after || 'om'} {antalAr} {c.yearsUnit})</span>
              </div>
            </div>
          </div>
        )}

        {/* Transparency Section */}
        {result && transparency && (
          <CalculatorTransparency
            assumptions={assumptions}
            howItWorks={transparency.howItWorks}
            sources={transparency.sources}
            didYouKnow={transparency.didYouKnow}
            relatedCalculators={transparency.relatedCalculators}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default SkogstillvaxtKalkylator;
